import React, { useState } from "react";
import "../App.css";
import {
  FaUser,
  FaPhoneAlt,
  FaEnvelope,
  FaClipboardList,
  FaTimes,
  FaCalendarCheck,
} from "react-icons/fa";
import logo from "../images/logo.png";
import { useCareModal } from "../context/CareModalContext";

const BookCareModal = () => {
  const { isBookCareOpen, closeBookCare } = useCareModal();

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    contactTime: "",
    careType: "",
    details: "",
  });

  const [errors, setErrors] = useState({});

  if (!isBookCareOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = "Full name is required.";
    if (!form.phone.trim()) newErrors.phone = "Contact number is required.";

    if (form.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email.trim())) {
        newErrors.email = "Please enter a valid email address.";
      }
    }

    if (!form.careType) newErrors.careType = "Please select the type of care.";

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "bookCare",
          payload: form,
        }),
      });

      if (!response.ok) {
        console.error("Failed to send care enquiry");
        alert(
          "Sorry, something went wrong sending your enquiry. Please try again."
        );
        return;
      }

      alert("Thank you – your care enquiry has been sent.");
      closeBookCare();
      setForm({
        name: "",
        phone: "",
        email: "",
        contactTime: "",
        careType: "",
        details: "",
      });
      setErrors({});
    } catch (err) {
      console.error("Error sending care enquiry:", err);
      alert(
        "Sorry, something went wrong sending your enquiry. Please try again."
      );
    }
  };

  return (
    <div className="modal-backdrop" onClick={closeBookCare}>
      <div
        className="modal book-care-modal modal-animate"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="modal-close-btn"
          onClick={closeBookCare}
          aria-label="Close"
        >
          <FaTimes />
        </button>

        <div className="book-care-header">
          <div className="book-care-logo-wrap">
            <img
              src={logo}
              alt="Ndils Daily Care Logo"
              className="book-care-logo"
            />
          </div>
          <div className="book-care-title-block">
            <h3>Book Care with Ndils Daily Care</h3>
            <p>
              Share a few details and our team will get in touch to discuss the
              best care plan for you or your loved one.
            </p>
          </div>
        </div>

        <form className="book-care-form" onSubmit={handleSubmit} noValidate>
          <div className="book-care-row">
            <div
              className={`book-care-field ${
                errors.name ? "field-error" : ""
              }`}
            >
              <label>
                <span className="field-icon">
                  <FaUser />
                </span>
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
              />
              {errors.name && <p className="error-text">{errors.name}</p>}
            </div>

            <div
              className={`book-care-field ${
                errors.phone ? "field-error" : ""
              }`}
            >
              <label>
                <span className="field-icon">
                  <FaPhoneAlt />
                </span>
                Contact Number
              </label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="+44 ..."
                required
              />
              {errors.phone && <p className="error-text">{errors.phone}</p>}
            </div>
          </div>

          <div className="book-care-row">
            <div
              className={`book-care-field ${
                errors.email ? "field-error" : ""
              }`}
            >
              <label>
                <span className="field-icon">
                  <FaEnvelope />
                </span>
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@example.com"
              />
              {errors.email && <p className="error-text">{errors.email}</p>}
            </div>

            <div className="book-care-field">
              <label>
                <span className="field-icon">
                  <FaCalendarCheck />
                </span>
                Preferred Contact Time
              </label>
              <select
                name="contactTime"
                value={form.contactTime}
                onChange={handleChange}
              >
                <option value="">Select an option</option>
                <option value="morning">Morning (8am – 12pm)</option>
                <option value="afternoon">Afternoon (12pm – 4pm)</option>
                <option value="evening">Evening (4pm – 7pm)</option>
              </select>
            </div>
          </div>

          <div
            className={`book-care-field ${
              errors.careType ? "field-error" : ""
            }`}
          >
            <label>
              <span className="field-icon">
                <FaClipboardList />
              </span>
              Type of Care Required
            </label>
            <select
              name="careType"
              value={form.careType}
              onChange={handleChange}
              required
            >
              <option value="">Please select</option>
              <option value="domiciliary">Domiciliary Care</option>
              <option value="supported-living">Supported Living</option>
              <option value="companionship">Companionship</option>
              <option value="not-sure">Not sure yet – need guidance</option>
            </select>
            {errors.careType && (
              <p className="error-text">{errors.careType}</p>
            )}
          </div>

          <div className="book-care-field">
            <label>Additional Details</label>
            <textarea
              name="details"
              rows="4"
              value={form.details}
              onChange={handleChange}
              placeholder="Tell us a bit about the support you’re looking for..."
            />
          </div>

          <div className="book-care-footer">
            <p className="book-care-note">
              We handle all information with strict confidentiality and in line
              with our privacy policy.
            </p>
            <button type="submit" className="btn btn-primary">
              Submit Care Enquiry
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookCareModal;
